import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselIndicators, CarouselItem } from '@connected/ui/components/carousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Image } from '@connected/ui/components/image';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { CheckmarkCircle } from '@/components/checkmark-circle';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { Observation } from '@/components/observation';
import {
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableHeader,
    ScrollableTableRow,
} from '@/components/scrollable-table';
import SearchInput from '@/components/search-input';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/final-circuit/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        search: z.string().optional().default(''),
    }),
});

export type InverterProps = components['schemas']['ListElectricalProjectFinalCircuitPortalQueryResultCircuit'];

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const search = Route.useSearch();

    const machinesQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.list({
        ElectricalProjectId: params.projectId ?? null,
        Kind: 'Machine',
    }));

    const pvInstallationQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.list({
        ElectricalProjectId: params.projectId ?? null,
        Kind: 'PhotovoltaicInverter',
    }));

    const spaceQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.list({
        ElectricalProjectId: params.projectId ?? null,
        Kind: 'Space',
    }));

    const buildingQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.list({
        ElectricalProjectId: params.projectId ?? null,
        Kind: 'Building',
    }));

    const otherQuery = useQuery(portaalQueryOptions.electrical.finalcircuits.list({
        ElectricalProjectId: params.projectId ?? null,
        Kind: 'Other',
    }));

    const observationQuery = useQuery(portaalQueryOptions.electrical.observations.list({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'FinalCircuit',
    }));
    const columns = useMemo<ColumnDef<InverterProps>[]>(() => [
        {
            accessorKey: 'Identifier',
            meta: {
                sticky: true,
                width: 200,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.machines.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="link">
                    <Link
                        to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId"
                        params={{
                            locationId: params.locationId,
                            projectId: params.projectId,
                            circuitId: row?.original?.ElectricalProjectFinalCircuitId ?? '',
                        }}
                    >
                        {row.original?.Identifier}
                    </Link>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'SevereObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.machines.list.severe')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.SevereObservationCount ?? 0) === 0)
                    return null;

                if ((board.SevereObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                circuitId: board.ElectricalProjectFinalCircuitId!,
                                observationId: board.SevereElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.Kind ?? t('module-electrical.machines.list.severe'),
                                    color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.HexColorCode ?? 'default',
                                    children: board.SevereObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.severe')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.Kind ?? t('module-electrical.machines.list.severe'),
                            color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.HexColorCode ?? 'default',
                            children: board.SevereObservationCount ?? 0,
                        }}
                        label={t('module-electrical.machines.list.severe')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.SevereObservationCount ?? 0;
                const countB = rowB.original.SevereObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'SeriousObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.machines.list.serious')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.SeriousObservationCount ?? 0) === 0)
                    return null;

                if ((board.SeriousObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                circuitId: board.ElectricalProjectFinalCircuitId!,
                                observationId: board.SeriousElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.Kind ?? t('module-electrical.machines.list.serious'),
                                    color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.HexColorCode ?? '#e9eaeb',
                                    children: board.SeriousObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.serious')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.Kind ?? t('module-electrical.machines.list.serious'),
                            color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.HexColorCode ?? '#e9eaeb',
                            children: board.SeriousObservationCount ?? 0,
                        }}
                        label={t('module-electrical.machines.list.serious')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.SeriousObservationCount ?? 0;
                const countB = rowB.original.SeriousObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.machines.list.minor')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.MinorObservationCount ?? 0) === 0)
                    return null;

                if ((board.MinorObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                circuitId: board.ElectricalProjectFinalCircuitId!,
                                observationId: board.MinorElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.Kind ?? t('module-electrical.machines.list.minor'),
                                    color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.HexColorCode ?? 'default',
                                    children: board.MinorObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.minor')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.Kind ?? t('module-electrical.machines.list.minor'),
                            color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.HexColorCode ?? 'default',
                            children: board.MinorObservationCount ?? 0,
                        }}
                        label={t('module-electrical.machines.list.minor')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.MinorObservationCount ?? 0;
                const countB = rowB.original.MinorObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'RemarkObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.machines.list.remarks')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.RemarkObservationCount ?? 0) === 0)
                    return null;

                if ((board.RemarkObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                circuitId: board.ElectricalProjectFinalCircuitId!,
                                observationId: board.RemarkElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.Kind ?? t('module-electrical.machines.list.remarks'),
                                    color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.HexColorCode ?? 'default',
                                    children: board.RemarkObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.remarks')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.Kind ?? t('module-electrical.machines.list.remarks'),
                            color: machinesQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.HexColorCode ?? 'default',
                            children: board.RemarkObservationCount ?? 0,
                        }}
                        label={t('module-electrical.machines.list.remarks')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.RemarkObservationCount ?? 0;
                const countB = rowB.original.RemarkObservationCount ?? 0;
                return countB - countA;
            },
        },
    ], [t, machinesQuery?.data?.Codes, params.locationId, params.projectId]);

    const finalCircuits = useMemo(() => {
        if (!machinesQuery.isFetched || !pvInstallationQuery.isFetched || !spaceQuery.isFetched || !buildingQuery.isFetched || !otherQuery.isFetched) {
            return [];
        }

        const allCircuits = [
            ...(machinesQuery.data?.FinalCircuits ?? []),
            ...(pvInstallationQuery.data?.FinalCircuits ?? []),
            ...(spaceQuery.data?.FinalCircuits ?? []),
            ...(buildingQuery.data?.FinalCircuits ?? []),
            ...(otherQuery.data?.FinalCircuits ?? []),
        ];

        return allCircuits.map((circuit, idx) => ({
            ...circuit,
            Identifier: circuit.Identifier ? circuit.Identifier : `Naamloos ${idx + 1}`,
        }));
    }, [
        machinesQuery.isFetched,
        pvInstallationQuery.isFetched,
        spaceQuery.isFetched,
        buildingQuery.isFetched,
        otherQuery.isFetched,
        machinesQuery.data,
        pvInstallationQuery.data,
        spaceQuery.data,
        buildingQuery.data,
        otherQuery.data,
    ]);

    if (machinesQuery.isLoading || observationQuery.isLoading || pvInstallationQuery.isLoading || spaceQuery.isLoading || buildingQuery.isLoading || otherQuery.isLoading) {
        return <LoadingPage />;
    }

    if (!finalCircuits) {
        return (
            <div className="flex items-center justify-center h-full">
                <p>{t('module-electrical.machines.no-data')}</p>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-9">
                <div className="w-full lg:w-1/2">
                    <DataTable.TableHead
                        title={t('module-electrical.control-boxes.title')}
                        label={`${finalCircuits?.length ?? 0} ${t(`module-electrical.control-boxes.title`)}`}
                        subtitle={t('module-electrical.control-boxes.subtitle')}
                    />
                    <div className="flex flex-col sm:flex-row gap-2 border-x border-t justify-start p-3">
                        <SearchInput route={Route} />
                    </div>
                    <DataTable
                        columns={columns}
                        data={finalCircuits?.filter(search.search ? x => x.Identifier?.toLocaleLowerCase().includes(search.search?.toLowerCase()) : x => x) || []}
                    />
                </div>
                <div className="w-full lg:w-1/2 flex flex-col gap-4">
                    {finalCircuits?.filter(search.search ? x => x.Identifier?.toLocaleLowerCase().includes(search.search?.toLowerCase()) : x => x).map(data => (
                        <div
                            key={data.ElectricalProjectFinalCircuitId}
                            className="border rounded-2xl p-4 md:p-5 cursor-pointer"
                        >
                            <Link
                                to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId"
                                params={{
                                    locationId: params.locationId,
                                    projectId: params.projectId,
                                    circuitId: data.ElectricalProjectFinalCircuitId!,
                                }}
                            >
                                <div className="flex justify-between gap-4">
                                    <h2 className="flex text-lg md:text-xl font-semibold">
                                        {data.Identifier ?? 'Onbekend'}
                                    </h2>
                                    <div className="flex flex-row gap-2">
                                        <p className="hidden sm:block text-sm">
                                            {t('module-electrical.machines.more')}
                                        </p>
                                        <ChevronRight size={20} />
                                    </div>
                                </div>
                                <p className="text-sm text-text-primary/60">
                                    {t('module-electrical.machines.floor')}
                                    {' '}
                                    {data.FloorNumber ?? 'Locatie onbekend'}
                                    ,
                                    {' '}
                                    {t('module-electrical.machines.room')}
                                    {' '}
                                    {data.SpaceNumber ?? 'Locatie onbekend'}
                                </p>
                            </Link>
                            <div className="flex flex-col md:flex-row gap-4 pt-4">
                                <div className="w-full md:w-1/3 h-full flex flex-col gap-4">
                                    <Carousel loop>
                                        <CarouselContent className="-ml-2 md:-ml-4">
                                            {data.Files?.slice(0, 5).map((file, idx) => (
                                                <CarouselItem className="pl-2 md:pl-4" key={file.ElectricalProjectFileId ?? idx}>
                                                    {file.File && (
                                                        <Image
                                                            previewer
                                                            className="h-52 w-auto object-contain rounded-lg"
                                                            alt={file?.File?.Name ?? ''}
                                                            queryOptions={portaalQueryOptions.files.electricalProject({
                                                                ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                                ImageQuality: 'OriginalWebp',
                                                            })}
                                                            enabled={!!file.ElectricalProjectFileId}
                                                        />
                                                    )}
                                                </CarouselItem>
                                            ))}
                                            {data.Files?.length === 0 && (
                                                <CarouselItem>
                                                    <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                                        <p className="text-center">{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                                    </div>
                                                </CarouselItem>
                                            )}
                                        </CarouselContent>
                                        <CarouselIndicators className="pb-0" />
                                    </Carousel>
                                    <div className="flex flex-col sm:flex-row justify-between gap-2 w-full">
                                        <Button
                                            variant="link"
                                            className="flex items-center gap-1 border px-5 rounded-lg h-10 w-full sm:w-1/2 text-foreground hover:no-underline hover:bg-background justify-center"
                                            onClick={() => setComingSoonModalOpen(true)}
                                        >
                                            <span className="text-sm">{t('module-electrical.machines.buttons.notification')}</span>
                                        </Button>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="flex items-center gap-2 border px-3 rounded-lg h-10 w-full sm:w-1/2 text-foreground hover:no-underline hover:bg-background justify-center">
                                                <span className="text-sm">{t('module-electrical.machines.buttons.request')}</span>
                                                <ChevronDown className="w-5 h-5" />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="flex flex-col" align="start">
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Onderhoud
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Power quality
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Offerte vervanging
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Thermografie
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                                    </div>
                                </div>
                                <div className="flex w-full md:w-2/3 justify-center rounded-tl-2xl rounded-tr-2xl overflow-hidden">
                                    <ScrollableTable maxHeight={15.3} className="rounded-none">
                                        <ScrollableTableHeader>
                                            <ScrollableTableRow className="h-12">
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.name',
                                                    )}
                                                </ScrollableTableHead>
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4 flex items-center justify-center">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.status',
                                                    )}
                                                </ScrollableTableHead>
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.date',
                                                    )}
                                                </ScrollableTableHead>
                                            </ScrollableTableRow>
                                        </ScrollableTableHeader>
                                        <ScrollableTableBody>
                                            {(() => {
                                                const circuitObservations = observationQuery?.data?.Observations?.filter(obs => obs.ElectricalProjectFinalCircuitId === data.ElectricalProjectFinalCircuitId) ?? [];
                                                if (circuitObservations.length === 0) {
                                                    return (
                                                        <div className="flex items-center justify-center h-12 text-text-primary/50 rounded-lg border-2 border-dashed w-full">
                                                            <p className="w-full text-center">{t('module-electrical.pv-installations.messages.noObservationsFound')}</p>
                                                        </div>
                                                    );
                                                }
                                                return circuitObservations.map(observation => (
                                                    <ScrollableTableRow
                                                        key={observation.ElectricalProjectObservationId}
                                                        className="cursor-pointer hover:bg-background-secondary h-12"
                                                    >
                                                        <ScrollableTableCell className="px-4">
                                                            <div className="flex items-center gap-2">
                                                                <Observation
                                                                    color={observation?.HexColorCode ?? ''}
                                                                    label={t('module-electrical.report.observations.details-table.columns.observation')}
                                                                    className="scale-75"
                                                                    children={[]}
                                                                />
                                                                <Link
                                                                    to="/customer/locations/$locationId/electrical/projects/$projectId/final-circuit/$circuitId/observations/$observationId"
                                                                    params={{
                                                                        locationId: params.locationId,
                                                                        projectId: params.projectId,
                                                                        circuitId: observation.ElectricalProjectFinalCircuitId!,
                                                                        observationId: observation.ElectricalProjectObservationId!,
                                                                    }}
                                                                    className="font-bold text-primary cursor-pointer"
                                                                >
                                                                    {t('module-electrical.report.observations.details-table.columns.observation')}
                                                                </Link>
                                                            </div>
                                                        </ScrollableTableCell>
                                                        <ScrollableTableCell className="px-4 flex items-center justify-center">
                                                            <CheckmarkCircle checked={observation.IsFixed} />
                                                        </ScrollableTableCell>
                                                        <ScrollableTableCell className="px-4">
                                                            {observation.Date ? new Date(observation.Date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                                                        </ScrollableTableCell>
                                                    </ScrollableTableRow>
                                                ));
                                            })()}
                                        </ScrollableTableBody>
                                        {/* No extra message outside table body, handled above */}
                                    </ScrollableTable>

                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
